// @wooconnect/shared — shared types, constants, and utilities
// ══════════ Enums (mirror DB enums) ══════════
export const UserRole = {
    SUPER_ADMIN: 'super_admin',
    STORE_OWNER: 'store_owner',
    MANAGER: 'manager',
    STAFF: 'staff',
};
export const UserStatus = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    SUSPENDED: 'suspended',
    READ_ONLY: 'read_only',
};
export const SiteStatus = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    ERROR: 'error',
    PENDING: 'pending',
};
export const PlanType = {
    FREE_TRIAL: 'free_trial',
    MONTHLY: 'monthly',
    YEARLY: 'yearly',
    LIFETIME: 'lifetime',
    CUSTOM: 'custom',
};
export const SubscriptionStatus = {
    ACTIVE: 'active',
    EXPIRING_SOON: 'expiring_soon',
    GRACE_PERIOD: 'grace_period',
    EXPIRED: 'expired',
    DISABLED: 'disabled',
};
export const NotificationType = {
    NEW_ORDER: 'new_order',
    ORDER_STATUS_CHANGED: 'order_status_changed',
    LOW_STOCK: 'low_stock',
    REFUND_REQUEST: 'refund_request',
    PRODUCT_BACKORDER: 'product_backorder',
    SYSTEM: 'system',
    AI_INSIGHT: 'ai_insight',
    SUBSCRIPTION_EXPIRING: 'subscription_expiring',
    SUBSCRIPTION_EXPIRED: 'subscription_expired',
    PLUGIN_REQUIRED: 'plugin_required',
    BRIDGE_OFFLINE: 'bridge_offline',
};
export const ThemePreference = {
    LIGHT: 'light',
    DARK: 'dark',
    SYSTEM: 'system',
};
// ══════════ Order Status Colors ══════════
export const ORDER_STATUS_COLORS = {
    pending: '#f97316',
    processing: '#3b82f6',
    'on-hold': '#eab308',
    completed: '#22c55e',
    cancelled: '#ef4444',
    refunded: '#a855f7',
    failed: '#ef4444',
};
// ══════════ Permission Keys ══════════
export const PERMISSION_KEYS = [
    'orders_view',
    'orders_edit',
    'orders_refund',
    'products_view',
    'products_edit',
    'customers_view',
    'analytics_view',
    'analytics_advanced',
    'notifications_enabled',
    'settings_view',
    'settings_edit',
    'ai_chat',
];
export const ROLE_DEFAULT_PERMISSIONS = {
    super_admin: {
        orders_view: true,
        orders_edit: true,
        orders_refund: true,
        products_view: true,
        products_edit: true,
        customers_view: true,
        analytics_view: true,
        analytics_advanced: true,
        notifications_enabled: true,
        settings_view: true,
        settings_edit: true,
        ai_chat: true,
    },
    store_owner: {
        orders_view: true,
        orders_edit: true,
        orders_refund: true,
        products_view: true,
        products_edit: true,
        customers_view: true,
        analytics_view: true,
        analytics_advanced: false,
        notifications_enabled: true,
        settings_view: true,
        settings_edit: true,
        ai_chat: true,
    },
    manager: {
        orders_view: true,
        orders_edit: true,
        orders_refund: false,
        products_view: true,
        products_edit: false,
        customers_view: true,
        analytics_view: false,
        analytics_advanced: false,
        notifications_enabled: true,
        settings_view: false,
        settings_edit: false,
        ai_chat: false,
    },
    staff: {
        orders_view: true,
        orders_edit: false,
        orders_refund: false,
        products_view: true,
        products_edit: false,
        customers_view: false,
        analytics_view: false,
        analytics_advanced: false,
        notifications_enabled: true,
        settings_view: false,
        settings_edit: false,
        ai_chat: false,
    },
};
// Maps webhook event names to notification_preferences column names
export const EVENT_TO_PREFERENCE = {
    new_order: 'newOrder',
    order_status_changed: 'orderStatusChanged',
    low_stock: 'lowStock',
    refund_created: 'refundRequest',
    product_backorder: 'productBackorder',
};
// ══════════ Order Types ══════════
export const OrderStatus = {
    PENDING: 'pending',
    PROCESSING: 'processing',
    ON_HOLD: 'on-hold',
    COMPLETED: 'completed',
    CANCELLED: 'cancelled',
    REFUNDED: 'refunded',
    FAILED: 'failed',
    TRASH: 'trash',
};
export const VALID_ORDER_STATUSES = [
    'pending', 'processing', 'on-hold', 'completed', 'cancelled', 'refunded', 'failed',
];
// ══════════ Permission Key Mapping ══════════
export const PERMISSION_KEY_TO_COLUMN = {
    orders_view: 'ordersView',
    orders_edit: 'ordersEdit',
    orders_refund: 'ordersRefund',
    products_view: 'productsView',
    products_edit: 'productsEdit',
    customers_view: 'customersView',
    analytics_view: 'analyticsView',
    analytics_advanced: 'analyticsAdvanced',
    notifications_enabled: 'notificationsEnabled',
    settings_view: 'settingsView',
    settings_edit: 'settingsEdit',
    ai_chat: 'aiChat',
};
// ══════════ Constants ══════════
export const APP_NAME = 'WooConnect';
export const APP_VERSION = '1.5.8';
export const BRIDGE_MIN_VERSION = '1.0.0';
export const CACHE_TTL = {
    DASHBOARD: 60,
    ORDERS: 60,
    PRODUCTS: 120,
    CUSTOMERS: 120,
    CATEGORIES: 300,
    UNREAD_COUNT: 30,
};
export const PAGINATION_DEFAULTS = {
    PAGE: 1,
    PER_PAGE: 20,
    MAX_PER_PAGE: 100,
};
// ══════════ Customer Types ══════════
export const CustomerSegment = {
    NEW: 'new',
    REPEAT: 'repeat',
    VIP: 'vip',
    INACTIVE: 'inactive',
};
export const DEFAULT_ANALYTICS_SETTINGS = {
    allStatuses: [],
    paidStatuses: ['wc-completed', 'wc-processing'],
    defaultPreset: 'thisMonth',
    compareDefaultPreset: 'previousPeriod',
    dateColumn: 'date_created',
    taxesEnabled: true,
    hposEnabled: false,
    weekStartsOn: 1,
    timezone: '',
    lastSyncedAt: null,
};
// ══════════ AI Types ══════════
export const AiFeature = {
    BUSINESS_PROFILE: 'business_profile',
    EMAIL_COMPOSE: 'email_compose',
    CHAT: 'chat',
    ORDER_INSIGHT: 'order_insight',
    PRODUCT_INSIGHT: 'product_insight',
    CUSTOMER_INSIGHT: 'customer_insight',
};
// ══════════ API Key Types ══════════
export const API_KEY_SCOPES = [
    'read:orders',
    'write:orders',
    'read:products',
    'write:products',
    'read:customers',
    'read:analytics',
    'read:dashboard',
    'read:notifications',
    'write:notifications',
];
// Maps API key scopes to permission keys
export const SCOPE_TO_PERMISSION = {
    'read:orders': 'orders_view',
    'write:orders': 'orders_edit',
    'read:products': 'products_view',
    'write:products': 'products_edit',
    'read:customers': 'customers_view',
    'read:analytics': 'analytics_view',
    'read:dashboard': 'orders_view',
    'read:notifications': 'notifications_enabled',
    'write:notifications': 'notifications_enabled',
};
// ══════════ Outgoing Webhook Types ══════════
export const OUTGOING_WEBHOOK_EVENTS = [
    'new_order',
    'order_status_changed',
    'low_stock',
    'refund_created',
    'product_backorder',
    'subscription_expiring',
    'subscription_expired',
    'bridge_offline',
];
//# sourceMappingURL=index.js.map